import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import { useParams } from "react-router-dom";
import { IconArrowLeft, IconDownload } from "@tabler/icons-react";

import { getCandidateResume, downloadResumeAPI } from "../../../services/resumeService";
import {
  extractProjects,
  extractAchievements,
  extractSkills,
} from "../../../utils/resumeParse";
import { getInitials } from "../Console/candidateRow";
import tokens from "../../theme/tokens";

const { color, font, radius, shadow } = tokens;

const SectionTitle = ({ children }) => (
  <Box
    sx={{
      mt: 4,
      mb: 1.5,
      font: `600 11px ${font.sans}`,
      color: color.textMuted,
      letterSpacing: ".07em",
    }}
  >
    {children}
  </Box>
);

const Card = ({ children }) => (
  <Box
    sx={{
      bgcolor: color.surface,
      border: `1px solid ${color.border}`,
      borderRadius: `${radius.card}px`,
      boxShadow: shadow.card,
      p: 2.25,
      mb: 1.25,
    }}
  >
    {children}
  </Box>
);

const Pill = ({ children }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      font: `400 11px ${font.sans}`,
      color: color.textSecondary,
      border: `1px solid ${color.borderInput}`,
      borderRadius: `${radius.pill}px`,
      px: 1.125,
      py: 0.375,
      mr: 0.75,
      mb: 0.75,
    }}
  >
    {children}
  </Box>
);

const Meta = ({ children }) => (
  <Box sx={{ font: `400 11px ${font.mono}`, color: color.textMuted }}>{children}</Box>
);

/**
 * Full resume for a candidate, in console styling. Opened in a new tab from the
 * console table and the detail panel.
 *
 * This shows what the resume SAYS. It deliberately carries no scores — those live in the
 * console's detail panel, which is fed by the rankings endpoint. This endpoint returns
 * no scoring data at all.
 */
const CandidateProfile = () => {
  const { jobId, resumeId } = useParams();
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    getCandidateResume(jobId, resumeId)
      .then((res) => {
        if (cancelled) return;
        if (!res?.Resume) throw new Error("No resume found for this candidate.");
        setResume(res.Resume);
        const name = res.Resume.candidate_info?.Name;
        if (name) document.title = `${name} — Resume`;
      })
      .catch((e) => !cancelled && setError(e.message || "Failed to load resume."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [jobId, resumeId]);

  const handleDownload = async () => {
    try {
      const url = await downloadResumeAPI(jobId, resumeId);
      window.open(url, "_blank", "noopener");
    } catch (_) {
      setError("Couldn't fetch the resume PDF.");
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error || !resume) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", px: 3 }}>
        <Box sx={{ textAlign: "center" }}>
          <Box sx={{ font: `600 16px ${font.sans}`, color: color.text, mb: 1 }}>
            Couldn't load this candidate
          </Box>
          <Box sx={{ font: `400 13px ${font.sans}`, color: color.textSecondary }}>{error}</Box>
        </Box>
      </Box>
    );
  }

  const { candidate_info = {}, education = [], work_experience = [], skill, additional_info } =
    resume;

  const projects = extractProjects(additional_info);
  const achievements = extractAchievements(additional_info);
  const skillCategories = extractSkills(skill);

  const email = candidate_info.Email || candidate_info.Contact?.Email;
  const phone = candidate_info.Phone || candidate_info.Contact?.Phone;

  return (
    <Box sx={{ minHeight: "100vh", py: 5, px: 3 }}>
      <Box sx={{ maxWidth: 760, mx: "auto" }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Button
            onClick={() => window.close()}
            startIcon={<IconArrowLeft size={15} stroke={1.75} />}
            sx={{ font: `500 12px ${font.sans}`, color: color.textSecondary }}
          >
            Back
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="outlined"
            onClick={handleDownload}
            startIcon={<IconDownload size={15} stroke={1.75} />}
          >
            Resume PDF
          </Button>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              flex: "none",
              display: "grid",
              placeItems: "center",
              borderRadius: `${radius.fileRow}px`,
              bgcolor: color.indigoTint,
              color: color.indigo,
              font: `600 17px ${font.sans}`,
            }}
          >
            {getInitials(candidate_info.Name)}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ font: `600 20px ${font.sans}`, color: color.text }}>
              {candidate_info.Name || "Unknown candidate"}
            </Box>
            {candidate_info.Title && (
              <Box sx={{ mt: 0.25, font: `400 13px ${font.sans}`, color: color.textSecondary }}>
                {candidate_info.Title}
              </Box>
            )}
            <Box sx={{ mt: 0.5 }}>
              <Meta>{[email, phone, candidate_info.GitHub].filter(Boolean).join("  ·  ") || "No contact details"}</Meta>
            </Box>
          </Box>
        </Box>

        {work_experience.length > 0 && (
          <>
            <SectionTitle>WORK EXPERIENCE</SectionTitle>
            {work_experience.map((exp, i) => (
              <Card key={i}>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
                  <Box sx={{ flex: 1, font: `600 14px ${font.sans}`, color: color.text }}>
                    {exp?.Job_Title || "Position not specified"}
                  </Box>
                  <Meta>{exp?.Duration || "—"}</Meta>
                </Box>
                <Box sx={{ mt: 0.25, font: `400 12.5px ${font.sans}`, color: color.textSecondary }}>
                  {exp?.Company || "Company not specified"}
                </Box>
                {Array.isArray(exp?.Responsibilities) && exp.Responsibilities.length > 0 && (
                  <Box
                    component="ul"
                    sx={{
                      mt: 1.25,
                      mb: 0,
                      pl: 2.25,
                      font: `400 12.5px/1.7 ${font.sans}`,
                      color: color.textSecondary,
                    }}
                  >
                    {exp.Responsibilities.map((r, j) => (
                      <li key={j}>{r}</li>
                    ))}
                  </Box>
                )}
              </Card>
            ))}
          </>
        )}

        {education.length > 0 && (
          <>
            <SectionTitle>EDUCATION</SectionTitle>
            {education.map((edu, i) => (
              <Card key={i}>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
                  <Box sx={{ flex: 1, font: `600 14px ${font.sans}`, color: color.text }}>
                    {edu?.Degree || "Degree not specified"}
                  </Box>
                  <Meta>{edu?.Duration || "—"}</Meta>
                </Box>
                <Box sx={{ mt: 0.25, font: `400 12.5px ${font.sans}`, color: color.textSecondary }}>
                  {edu?.Institution || "Institution not specified"}
                  {edu?.Major ? ` · ${edu.Major}` : ""}
                </Box>
                {(edu?.GPA || edu?.CGPA || edu?.Percentage) && (
                  <Box sx={{ mt: 0.5 }}>
                    <Meta>
                      {edu.GPA
                        ? `GPA ${edu.GPA}`
                        : edu.CGPA
                        ? `CGPA ${edu.CGPA}`
                        : `${edu.Percentage}%`}
                    </Meta>
                  </Box>
                )}
                {Array.isArray(edu?.Coursework) && edu.Coursework.length > 0 && (
                  <Box sx={{ mt: 1.25 }}>
                    {edu.Coursework.map((c, j) => (
                      <Pill key={j}>{c}</Pill>
                    ))}
                  </Box>
                )}
              </Card>
            ))}
          </>
        )}

        {skillCategories.length > 0 && (
          <>
            <SectionTitle>SKILLS</SectionTitle>
            <Card>
              {skillCategories.map((cat, i) => (
                <Box key={i} sx={{ mb: i === skillCategories.length - 1 ? 0 : 2 }}>
                  <Box
                    sx={{
                      mb: 0.875,
                      font: `500 12px ${font.sans}`,
                      color: color.textSecondary,
                    }}
                  >
                    {cat.category}
                  </Box>
                  {cat.skills.map((s, j) => (
                    <Pill key={j}>{s}</Pill>
                  ))}
                </Box>
              ))}
            </Card>
          </>
        )}

        {projects.length > 0 && (
          <>
            <SectionTitle>PROJECTS</SectionTitle>
            {projects.map((p, i) => (
              <Card key={i}>
                <Box sx={{ font: `600 14px ${font.sans}`, color: color.text }}>
                  {p?.Name || "Untitled project"}
                </Box>
                {p?.Description && (
                  <Box
                    sx={{
                      mt: 0.75,
                      font: `400 12.5px/1.7 ${font.sans}`,
                      color: color.textSecondary,
                    }}
                  >
                    {p.Description}
                  </Box>
                )}
                {Array.isArray(p?.Technologies) && p.Technologies.length > 0 && (
                  <Box sx={{ mt: 1.25 }}>
                    {p.Technologies.map((t, j) => (
                      <Pill key={j}>{t}</Pill>
                    ))}
                  </Box>
                )}
              </Card>
            ))}
          </>
        )}

        {achievements.length > 0 && (
          <>
            <SectionTitle>ACHIEVEMENTS</SectionTitle>
            <Card>
              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: 2.25,
                  font: `400 12.5px/1.8 ${font.sans}`,
                  color: color.textSecondary,
                }}
              >
                {achievements.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </Box>
            </Card>
          </>
        )}
      </Box>
    </Box>
  );
};

export default CandidateProfile;
